import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { SidebarNavMenuItem } from '../../../decorators/sidebar-nav-menu-item';
import { UserEntity } from '../../../models/entities/user-entity';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { NotificationService } from '../../../services/notification.service';
import { UserEntityService } from '../../../services/settings/user-entity.service';
import { AuthService } from '../../../services/auth.service';
import { Log } from '../../../services/logger.service';
import { ConfirmDialogService } from '../../../services/confirm-dialog.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { PARTICIPANTS_TEAM_ID, PARTICIPANTS_TEAM_NAME } from '../../../utils/auth.utils';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../shared/pagination-options';


interface Team {
  id: string;
  name: string;
}
interface Role {
  value: string;
  label: string;
}

interface DeleteUserOutcome {
  userId: string;
  loginId: string;
  name: string;
  deleted: boolean;
  reasonCode: string;
  message: string;
}

interface DeleteUsersResponse {
  requestedCount: number;
  deletedCount: number;
  skippedCount: number;
  outcomes: DeleteUserOutcome[];
}

@SidebarNavMenuItem({
  route: '/users',
  faIcon: 'manage_accounts',
  name: 'Users',
  order: 2100,
  businessProcessesName: 'Users'
})
@Component({
  selector: 'app-user-creation',
  templateUrl: './user-creation.component.html',
  styleUrls: ['./user-creation.component.css']
})
export class UserCreationComponent implements OnInit, AfterViewInit {
  private readonly logger = Log.create(UserCreationComponent);

  openAddTeamModal() {
    this.router.navigate(['/teams']);
  }

  selectedUserId: string | undefined = undefined;
  viewMode: 'detail' | 'card' = 'detail';
  displayedColumns: string[] = ['name', 'loginId', 'teams', 'roles', 'actions'];
  public isLoading = false;
  public isDeletingUsers = false;
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;
  get isCardView(): boolean {
    return this.viewMode === 'card';
  }

  users: UserEntity[] = [];
  dataSource = new MatTableDataSource<UserEntity>([]);
  selection = new SelectionModel<UserEntity>(true, []);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  userForm!: FormGroup;
  showAddModal = false;
  showEditModal = false;
  showDetailModal = false;
  selectedUser: UserEntity | null = null;
  isEditMode = false;
  searchTerm = '';
  loading = false;
  error: string | null = null;

  // Dropdown options
  availableRoles: Role[] = [
    // { value: 'admin', label: 'Admin' },
    // { value: 'sales', label: 'Sales' },
    // { value: 'manager', label: 'Manager' },
    // { value: 'user', label: 'User' },
    // { value: 'support', label: 'Support' }
  ];

  availableTeams: Team[] = [
    // { value: 'Sales', label: 'Sales', teamId: '7864' },
    // { value: 'Marketing', label: 'Marketing', teamId: '7865' },
    // { value: 'Development', label: 'Development', teamId: '7866' },
    // { value: 'Support', label: 'Support', teamId: '7867' },
    // { value: 'HR', label: 'HR', teamId: '7868' }
  ];

  isRoleDropdownOpen = false;

  constructor(
    private fb: FormBuilder,
    private userService: UserEntityService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private notification: NotificationService,
    private authService: AuthService
  ) {
    this.logger.i('FormBuilder instance:', fb);
    // Don't call initializeForm here - FormBuilder might not be ready
  }

  // to change the view mode
  onViewChange(value: string) {
    this.viewMode = value as 'detail' | 'card';
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnInit(): void {
    this.configureDisplayedColumns();
    this.initializeForm();
    this.loadUsers();
    this.loadMasterData();
    this.setupFormWatchers();

    // Close dropdown when clicking outside
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.multi-select-container')) {
        this.isRoleDropdownOpen = false;
      }
      if (!target.closest('.actions') && !target.closest('.dropdown-menu')) {
        this.selectedUserId = undefined;
      }
    });
  }

  get canDeleteParticipants(): boolean {
    return this.authService.canArchive();
  }

  private configureDisplayedColumns(): void {
    if (!this.canDeleteParticipants) {
      this.displayedColumns = ['name', 'loginId', 'teams', 'roles', 'actions'];
      return;
    }

    this.displayedColumns = ['select', 'name', 'loginId', 'teams', 'roles', 'actions'];
  }

  private _filterTeams(value: string | null): Team[] {
    this.logger.i('_filterTeams called with value:', value);
    this.logger.i('availableTeams:', this.availableTeams);
    this.logger.i('selectedTeams:', this.selectedTeams);

    if (!this.availableTeams || this.availableTeams.length === 0) {
      this.logger.w('No available teams!');
      return [];
    }

    // Filter out already selected teams
    const available = this.availableTeams.filter(team =>
      !this.selectedTeams.some(st => st.teamId === team.id)
    );
    this.logger.i('Available teams after filtering selected:', available);

    // If no search value, return all available teams
    if (!value) {
      this.logger.i('No search value, returning all available teams');
      return available;
    }

    // Filter by search term
    const filterValue = value.toLowerCase();
    const filtered = available.filter(team =>
      team.name.toLowerCase().includes(filterValue)
    );
    this.logger.i('Filtered teams by search term:', filtered);
    return filtered;
  }

  private _filterRoles(value: string | null): Role[] {
    this.logger.i('_filterRoles called with value:', value);

    if (!this.availableRoles || this.availableRoles.length === 0) {
      this.logger.w('No available roles!');
      return [];
    }

    // Filter out already selected roles
    const available = this.availableRoles.filter(role =>
      !this.selectedRoles.includes(role.value)
    );
    this.logger.i('Available roles after filtering selected:', available);

    // If no search value, return all available roles
    if (!value) {
      this.logger.i('No search value, returning all available roles');
      return available;
    }

    // Filter by search term
    const filterValue = value.toLowerCase();
    const filtered = available.filter(role =>
      role.label.toLowerCase().includes(filterValue)
    );
    this.logger.i('Filtered roles by search term:', filtered);
    return filtered;
  }

  initializeForm(): void {
    this.userForm = this.fb.group({
      loginId: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(3)]],
      name: ['', [Validators.required]],
      roles: [[], [Validators.required]], // Will be bound to mat-selection-list
      teams: [[], [Validators.required]],
      team: [''],  // Keep for backward compatibility
      teamId: ['']  // Keep for backward compatibility
    });
  }

  // Selected teams for multi-team support
  selectedTeams: Array<{teamId: string, team: string, isPrimary: boolean}> = [];
  currentTeamSelection: string = '';

  // Material autocomplete for teams
  teamCtrl = new FormControl('');
  filteredTeams!: Observable<Team[]>;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  @ViewChild('teamInput') teamInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  // Material autocomplete for roles
  roleCtrl = new FormControl('');
  filteredRoles!: Observable<Role[]>;
  selectedRoles: string[] = [];
  @ViewChild('roleInput') roleInput!: ElementRef<HTMLInputElement>;

  setupFormWatchers(): void {
    // Form watchers for multi-team support
    // Team validation happens when adding teams
  }

  onTeamInputFocus(): void {
    // Trigger autocomplete dropdown by setting null value
    // This shows all available teams when clicking the input
    this.logger.i('onTeamInputFocus called');
    this.logger.i('Available teams:', this.availableTeams);
    this.logger.i('Selected teams:', this.selectedTeams);
    this.logger.i('TeamCtrl value:', this.teamCtrl.value);

    // Force the observable to emit with null to show all options
    this.teamCtrl.setValue(null);

    // Manually open the autocomplete panel
    setTimeout(() => {
      if (this.autocompleteTrigger) {
        this.logger.i('Opening autocomplete panel manually');
        this.autocompleteTrigger.openPanel();
      }
    }, 0);
  }

  selectTeam(event: MatAutocompleteSelectedEvent): void {
    const selectedTeamId = event.option.value;
    const selectedTeam = this.availableTeams.find(t => t.id === selectedTeamId);

    if (!selectedTeam) return;

    // Check if already added
    if (this.selectedTeams.some(t => t.teamId === selectedTeam.id)) {
      this.notification.notify('This team is already added', 'warning', 2000);
      return;
    }

    // If this is the first team, make it primary
    const isPrimary = this.selectedTeams.length === 0;

    this.selectedTeams.push({
      teamId: selectedTeam.id,
      team: selectedTeam.name,
      isPrimary: isPrimary
    });

    // Update form control
    this.userForm.patchValue({ teams: this.selectedTeams });

    // For backward compatibility, set primary team in old fields
    if (isPrimary) {
      this.userForm.patchValue({
        team: selectedTeam.name,
        teamId: selectedTeam.id
      });
    }

    // Clear input and trigger filter update
    if (this.teamInput) {
      this.teamInput.nativeElement.value = '';
    }
    this.teamCtrl.setValue('');
  }

  selectRole(event: MatAutocompleteSelectedEvent): void {
    const selectedRoleValue = event.option.value;
    const selectedRole = this.availableRoles.find(r => r.value === selectedRoleValue);

    if (!selectedRole) return;

    // Check if already added
    if (this.selectedRoles.includes(selectedRole.value)) {
      this.notification.notify('This role is already added', 'warning', 2000);
      return;
    }

    this.selectedRoles.push(selectedRole.value);

    // Update form control
    this.userForm.patchValue({ roles: this.selectedRoles });

    // Clear input
    if (this.roleInput) {
      this.roleInput.nativeElement.value = '';
    }

    // Trigger filter update to remove selected role from dropdown
    this.roleCtrl.setValue('');
  }

  onRoleInputFocus(): void {
    this.logger.i('onRoleInputFocus called');
    this.roleCtrl.setValue(null);
  }

  loadUsers(): void {
    this.loading = true;
    this.error = null;

    // For demo purposes, using sample data
    // Replace this with actual API call when ready:
    this.userService.getAllUsers().subscribe({
      next: (result) => {
        this.logger.i('getAllUsers raw response:', result);
        // Belt-and-suspenders: strip any ad-hoc users the backend may have returned
        // (e.g. dev/test environments or rollback scenarios).
        this.users = (result as any[]).filter(
          u => !u.details?.CreatedAdHoc && !u.Details?.CreatedAdHoc
        ) as UserEntity[];
        this.dataSource.data = [...this.users];
        this.selection.clear();
        this.logger.i('Loaded users count:', this.users.length);
        // Log teams for each user
        this.users.forEach(user => {
          this.logger.i(`User ${user.name} teams:`, user.teams);
        });
        this.loading = false;
      },
      error: (err) => {
        this.logger.e('Error loading users:', err);
        this.loading = false;
      }
    });

  }

  loadMasterData(): void {
    this.loading = true;
    this.error = null;

    // For demo purposes, using sample data
    // Replace this with actual API call when ready:
    this.userService.getMasterData().subscribe({
      next: (result) => {
        // this.availableRoles = result.Roles;
        this.availableRoles = result.Roles.map((r: string) => ({ value: r, label: r }));
        this.availableTeams = result.Teams;
        this.logger.i('Loaded masterdata:', result);
        this.logger.i('Available teams after load:', this.availableTeams);
        this.logger.i('Number of teams:', this.availableTeams?.length);

        // Initialize team autocomplete AFTER teams are loaded
        this.filteredTeams = this.teamCtrl.valueChanges.pipe(
          startWith(null),
          map(value => {
            this.logger.i('Filter teams called with value:', value);
            const filtered = this._filterTeams(value);
            this.logger.i('Filtered teams:', filtered);
            return filtered;
          })
        );

        // Initialize role autocomplete AFTER roles are loaded
        this.filteredRoles = this.roleCtrl.valueChanges.pipe(
          startWith(null),
          map(value => {
            this.logger.i('Filter roles called with value:', value);
            const filtered = this._filterRoles(value);
            this.logger.i('Filtered roles:', filtered);
            return filtered;
          })
        );
      },
      error: (err) => {
        this.logger.e('Error loading users:', err);
      }
    });

    this.dataSource.data = [...this.users];
    this.loading = false;

  }

  openAddModal(): void {
    this.logger.i('=== openAddModal called ===');
    this.isEditMode = false;
    this.selectedTeams = [];
    this.selectedRoles = [];
    this.currentTeamSelection = '';
    this.userForm.reset();
    this.userForm.patchValue({ roles: [], teams: [] });
    this.showAddModal = true;
    this.showEditModal = false;
    this.logger.i('Modal flags - showAddModal:', this.showAddModal, 'showEditModal:', this.showEditModal);

    // Reinitialize autocomplete to ensure it works
    this.logger.i('Available teams before reinit:', this.availableTeams?.length);
    this.teamCtrl.setValue(null);
    this.roleCtrl.setValue(null);
    if (this.availableTeams && this.availableTeams.length > 0) {
      this.logger.i('Reinitializing filteredTeams observable in openAddModal');
      this.filteredTeams = this.teamCtrl.valueChanges.pipe(
        startWith(null),
        map(value => {
          this.logger.i('[ADD MODAL] Filter called with:', value);
          const result = this._filterTeams(value);
          this.logger.i('[ADD MODAL] Filtered result:', result);
          return result;
        })
      );
    } else {
      this.logger.e('No available teams to initialize autocomplete!');
    }
    if (this.availableRoles && this.availableRoles.length > 0) {
      this.filteredRoles = this.roleCtrl.valueChanges.pipe(
        startWith(null),
        map(value => this._filterRoles(value))
      );
    }
  }

  openEditModal(user: UserEntity): void {
    this.logger.i('=== openEditModal called ===');
    this.logger.i('User to edit:', user);
    this.isEditMode = true;
    this.selectedUser = user;
    this.showEditModal = true;
    this.showAddModal = false;
    this.logger.i('Modal flags - showAddModal:', this.showAddModal, 'showEditModal:', this.showEditModal);

    // Load teams from user entity
    if (user.teams && user.teams.length > 0) {
      this.selectedTeams = user.teams.map(t => ({
        teamId: t.teamId,
        team: t.team,
        isPrimary: t.isPrimary
      }));
    } else if (user.teamId && user.team) {
      // Backward compatibility: if only old single team exists
      this.selectedTeams = [{
        teamId: user.teamId,
        team: user.team,
        isPrimary: true
      }];
    } else {
      this.selectedTeams = [];
    }

    // Load roles from user entity
    this.selectedRoles = user.roles || [];

    this.userForm.patchValue({
      loginId: user.loginId,
      password: user.password,
      name: user.name,
      roles: this.selectedRoles,
      teams: this.selectedTeams,
      team: user.team,
      teamId: user.teamId
    });

    // Trigger autocomplete filter update to exclude already selected teams
    this.logger.i('Selected teams for edit:', this.selectedTeams);
    this.logger.i('Available teams before reinit:', this.availableTeams?.length);
    this.teamCtrl.setValue(null);
    this.roleCtrl.setValue(null);
    if (this.availableTeams && this.availableTeams.length > 0) {
      this.logger.i('Reinitializing filteredTeams observable in openEditModal');
      this.filteredTeams = this.teamCtrl.valueChanges.pipe(
        startWith(null),
        map(value => {
          this.logger.i('[EDIT MODAL] Filter called with:', value);
          const result = this._filterTeams(value);
          this.logger.i('[EDIT MODAL] Filtered result:', result);
          return result;
        })
      );
    } else {
      this.logger.e('No available teams to initialize autocomplete!');
    }
    if (this.availableRoles && this.availableRoles.length > 0) {
      this.filteredRoles = this.roleCtrl.valueChanges.pipe(
        startWith(null),
        map(value => this._filterRoles(value))
      );
    }
  }

  openDetailModal(user: UserEntity): void {
    this.selectedUser = user;
    this.showDetailModal = true;
    this.selectedUserId = undefined; // Add this line
  }

  closeModals(): void {
    this.showAddModal = false;
    this.showEditModal = false;
    this.showDetailModal = false;
    this.selectedUser = null;
    this.selectedTeams = [];
    this.currentTeamSelection = '';
    this.userForm.reset();
    this.error = null;
    this.isRoleDropdownOpen = false;
  }

  removeTeamFromUser(index: number): void {
    const removedTeam = this.selectedTeams[index];
    this.selectedTeams.splice(index, 1);

    // If removed team was primary and there are other teams, make first one primary
    if (removedTeam.isPrimary && this.selectedTeams.length > 0) {
      this.selectedTeams[0].isPrimary = true;
      this.userForm.patchValue({
        team: this.selectedTeams[0].team,
        teamId: this.selectedTeams[0].teamId
      });
    }

    this.userForm.patchValue({ teams: this.selectedTeams });

    // Trigger autocomplete filter update to show the removed team again
    this.teamCtrl.updateValueAndValidity();
  }

  setPrimaryTeam(index: number): void {
    // Set all to non-primary
    this.selectedTeams.forEach(t => t.isPrimary = false);
    // Set selected as primary
    this.selectedTeams[index].isPrimary = true;

    // Update backward compatibility fields
    this.userForm.patchValue({
      team: this.selectedTeams[index].team,
      teamId: this.selectedTeams[index].teamId,
      teams: this.selectedTeams
    });
  }

  onSubmit(): void {
    // Validate teams array
    if (this.selectedTeams.length === 0) {
      this.notification.notify('Please add at least one team', 'warning', 2000);
      this.userForm.get('teams')?.markAsTouched();
      return;
    }

    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const loginId = this.userForm.value.loginId;

    // Duplicate check
    const exists = this.dataSource.data.some(
      u => u.loginId.toLowerCase() === loginId.toLowerCase() && (!this.isEditMode || u.id !== this.selectedUser?.id)
    );

    if (exists) {
      this.notification.notify("Login ID already exists! Please use a different Login ID.");
      return;
    }

    // Prepare form data with teams
    const formData = {
      ...this.userForm.value,
      teams: this.selectedTeams.map(t => ({
        teamId: t.teamId,
        team: t.team,
        isPrimary: t.isPrimary,
        addedDate: new Date().toISOString()
      }))
    };

    if (this.isEditMode && this.selectedUser) {
      this.updateUser(formData);
    } else {
      this.addUser(formData);
    }
  }

  async addUser(userData: any): Promise<void> {
    this.loading = true;
    this.error = null;

    // For demo purposes, just add to local array
    // Replace with actual API call when ready
    const newUser: UserEntity = {
      _id: { $oid: this.generateId() },
      ...userData
    };

    this.users.push(newUser);
    this.filterUsers();
    // this.closeModals();
    this.loading = false;



    // Uncomment when API is ready:
    const created = await this.userService.createUser(userData);
    if (created) {
      this.notification.notify('User created successfully', 'success', 2000);
    }
    else {
      this.error = 'Failed to create user';
      this.notification.notify('Failed to create user', 'error', 2000);
    }
    // Refresh users from API
    this.loadUsers();

    this.closeModals();
    this.logger.i('Created User Record:', created);

  }

  updateUser(userData: any): void {

    this.logger.i('Updating user with data:', userData);
    if (!this.selectedUser) return;

    // Capture selectedUser reference before async operations
    const selectedUserId = this.selectedUser.id;
    const userName = this.selectedUser.name || 'this user';

    // Show confirmation dialog
    this.confirmDialog.confirmAction(
      'Confirm Update',
      `Update user "${userName}"?`,
      'warning',
      'Update',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      this.loading = true;
      this.error = null;

      // Set ID for update
      userData.id = selectedUserId;

      // Call API first, only update UI on success
      this.userService.updateUser(userData).subscribe({
        next: (updatedUser) => {
          this.logger.i('User updated successfully, response:', updatedUser);
          // Reload users from server to ensure we have the latest data
          this.loadUsers();
          this.closeModals();
          this.loading = false;
          this.notification.notify('User updated successfully', 'success', 2000);
        },
        error: (error) => {
          this.error = 'Failed to update user';
          this.loading = false;
          this.logger.e('Error updating user:', error);

          // Extract error message from response
          let errorMessage = 'Failed to update user';
          if (error.error && typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.notification.notify(errorMessage, 'error', 5000);
        }
      });
    });

  }

  deleteUser(user: UserEntity): void {
    if (!this.canDeleteParticipants) {
      return;
    }

    this.requestDelete([user]);
  }

  onSearch(): void {
    this.filterUsers();
  }

  filterUsers(): void {
    const term = this.searchTerm.toLowerCase().trim();
    this.dataSource.data = term
      ? this.users.filter(user => {
          if (user.name?.toLowerCase().includes(term)) return true;
          if (user.loginId?.toLowerCase().includes(term)) return true;
          if (user.team?.toLowerCase().includes(term)) return true;
          if (user.teams?.some((t: any) => t.team?.toLowerCase().includes(term))) return true;
          if (user.roles?.some((r: string) => r.toLowerCase().includes(term))) return true;
          return false;
        })
      : [...this.users];
    this.selection.clear();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  isDeletionEligible(user: UserEntity): boolean {
    return this.hasSingleRole(user) && this.isParticipantTeamOnly(user);
  }

  private hasSingleRole(user: UserEntity): boolean {
    const roles = (user.roles ?? []).map(r => (r ?? '').trim().toLowerCase()).filter(r => r.length > 0);
    return roles.length === 1;
  }

  private normalizeUserTeams(user: UserEntity): Array<{ teamId: string; team: string }> {
    if ((user.teams ?? []).length > 0) {
      return (user.teams ?? []).map(t => ({
        teamId: (t.teamId ?? '').trim(),
        team: (t.team ?? '').trim()
      }));
    }

    const legacyTeamId = (user.teamId ?? '').trim();
    const legacyTeam = (user.team ?? '').trim();
    if (!legacyTeamId && !legacyTeam) {
      return [];
    }

    return [{ teamId: legacyTeamId, team: legacyTeam }];
  }

  private isParticipantTeamOnly(user: UserEntity): boolean {
    const teams = this.normalizeUserTeams(user);
    if (teams.length !== 1) {
      return false;
    }

    const team = teams[0];
    const idMatches = team.teamId === PARTICIPANTS_TEAM_ID;
    const nameMatches = team.team.localeCompare(PARTICIPANTS_TEAM_NAME, undefined, { sensitivity: 'accent' }) === 0;

    if (!team.teamId) {
      return nameMatches;
    }

    if (!team.team) {
      return idMatches;
    }

    return idMatches && nameMatches;
  }

  isSelected(user: UserEntity): boolean {
    return this.selection.isSelected(user);
  }

  toggleUserSelection(user: UserEntity): void {
    if (!this.canDeleteParticipants) {
      return;
    }

    if (!this.isDeletionEligible(user)) {
      this.notification.notify('Only participant-only users are eligible for deletion.', 'warning', 2500);
      return;
    }

    this.selection.toggle(user);
  }

  masterToggle(): void {
    if (!this.canDeleteParticipants) {
      return;
    }

    const eligibleRows = this.dataSource.data.filter(user => this.isDeletionEligible(user));
    const allSelected = eligibleRows.length > 0 && eligibleRows.every(user => this.selection.isSelected(user));
    if (allSelected) {
      eligibleRows.forEach(user => this.selection.deselect(user));
      return;
    }

    eligibleRows.forEach(user => this.selection.select(user));
  }

  allEligibleRowsSelected(): boolean {
    const eligibleRows = this.dataSource.data.filter(user => this.isDeletionEligible(user));
    if (eligibleRows.length === 0) {
      return false;
    }

    return eligibleRows.every(user => this.selection.isSelected(user));
  }

  get hasSelection(): boolean {
    return this.selection.selected.length > 0;
  }

  requestDeleteSelected(): void {
    if (!this.canDeleteParticipants) {
      return;
    }

    if (this.selection.selected.length === 0) {
      this.notification.notify('Select at least one participant-only user to delete.', 'warning', 2500);
      return;
    }

    this.requestDelete([...this.selection.selected]);
  }

  private requestDelete(usersToDelete: UserEntity[]): void {
    if (usersToDelete.length === 0) {
      return;
    }

    const ineligibleUsers = usersToDelete.filter(user => !this.isDeletionEligible(user));
    if (ineligibleUsers.length > 0) {
      this.notification.notify('Selection contains users that are not participant-only. Remove them and retry.', 'warning', 3000);
      return;
    }

    const displayNames = usersToDelete.map(user => user.name || user.loginId || user.id);
    const topFive = displayNames.slice(0, 5).map(name => `Delete: ${name}`);
    const overflow = displayNames.slice(5);
    const hoverLabel = overflow.length > 0 ? `+${overflow.length} more users` : undefined;
    const hoverText = overflow.length > 0 ? overflow.join('\n') : undefined;

    this.confirmDialog.typedConfirm({
      title: usersToDelete.length === 1 ? 'Delete User' : `Delete ${usersToDelete.length} Users`,
      message: 'This action permanently deletes participant users. This cannot be undone.',
      details: topFive,
      overflowHoverLabel: hoverLabel,
      overflowHoverText: hoverText,
      type: 'error',
      confirmPhrase: usersToDelete.length === 1 ? 'DELETE USER' : 'DELETE USERS',
      confirmButtonText: usersToDelete.length === 1 ? 'Delete User' : 'Delete Users',
      confirmSummary: 'Only selected participant users will be deleted.'
    }).subscribe(result => {
      if (!result.confirmed) {
        return;
      }

      this.executeDeleteUsers(usersToDelete);
    });
  }

  private executeDeleteUsers(usersToDelete: UserEntity[]): void {
    const userIds = usersToDelete.map(user => user.id).filter(id => !!id);
    if (userIds.length === 0) {
      this.notification.notify('No valid user IDs selected.', 'warning', 2500);
      return;
    }

    this.isDeletingUsers = true;
    this.userService.deleteUsers(userIds).subscribe({
      next: (result: DeleteUsersResponse) => {
        const deletedCount = result?.deletedCount ?? 0;
        const skippedCount = result?.skippedCount ?? 0;
        const summary = `Deleted ${deletedCount} user(s), skipped ${skippedCount}.`;
        const notifType = skippedCount > 0 ? 'warning' : 'success';
        this.notification.notify(summary, notifType, 3500);
        this.selection.clear();
        this.loadUsers();
        this.isDeletingUsers = false;
      },
      error: (err) => {
        this.logger.e('Error deleting users:', err);
        this.isDeletingUsers = false;
        this.notification.notify('Failed to delete users.', 'error', 3500);
      }
    });
  }

  onRoleChange(event: any, roleValue: string): void {
    const currentRoles = this.userForm.get('roles')?.value || [];

    if (event.target.checked) {
      // Add role if checked
      if (!currentRoles.includes(roleValue)) {
        const updatedRoles = [...currentRoles, roleValue];
        this.userForm.patchValue({ roles: updatedRoles });
      }
    } else {
      // Remove role if unchecked
      const updatedRoles = currentRoles.filter((role: string) => role !== roleValue);
      this.userForm.patchValue({ roles: updatedRoles });
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      control?.markAsTouched();
    });
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 24);
  }

  getRoleLabels(roles: string[]): string {
    return roles.map(role => {
      const roleObj = this.availableRoles.find(r => r.value === role);
      return roleObj ? roleObj.label : role;
    }).join(', ');
  }

  getTeamNames(user: UserEntity): string {
    if (user.teams && user.teams.length > 0) {
      return user.teams.map(t => t.team + (t.isPrimary ? ' (Primary)' : '')).join(', ');
    }
    return user.team || 'No team';
  }

  // Helper methods for form validation
  isFieldInvalid(fieldName: string): boolean {
    const field = this.userForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.userForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['email']) return 'Please enter a valid email';
      if (field.errors['minlength']) return `${fieldName} must be at least ${field.errors['minlength'].requiredLength} characters`;
    }
    return '';
  }


  toggleMenu(event: Event, user: any): void {
    event.stopPropagation();
    this.logger.i('Toggle menu clicked for user:', user.id); // Debug log

    if (this.selectedUserId !== user.id) {
      this.selectedUserId = user.id;
    } else {
      this.selectedUserId = undefined;
    }

    this.logger.i('Selected user Id:', this.selectedUserId); // Debug log
  }


  toggleRoleDropdown(): void {
    this.isRoleDropdownOpen = !this.isRoleDropdownOpen;
  }

  toggleRole(role: Role, event: Event): void {
    event.stopPropagation();
    const currentRoles = this.userForm.get('roles')?.value || [];

    if (this.isRoleSelected(role.value)) {
      this.userForm.patchValue({ roles: currentRoles.filter((r: string) => r !== role.value) });
    } else {
      this.userForm.patchValue({ roles: [...currentRoles, role.value] });
    }
  }

  removeRole(index: number): void {
    this.selectedRoles.splice(index, 1);
    this.userForm.patchValue({ roles: this.selectedRoles });
    // Trigger filter update to show removed role in dropdown again
    this.roleCtrl.setValue(null);
  }

  isRoleSelected(roleValue: string): boolean {
    const currentRoles = this.userForm.get('roles')?.value || [];
    return currentRoles.includes(roleValue);
  }

  getSelectedRoles(): Role[] {
    const currentRoles = this.userForm.get('roles')?.value || [];
    return this.availableRoles.filter(role => currentRoles.includes(role.value));
  }


  toggleRole1(roleValue: string, event: Event): void {
    event.stopPropagation();

    const currentRoles = this.userForm.get('roles')?.value || [];

    if (this.isRoleSelected(roleValue)) {
      // Remove role
      const updatedRoles = currentRoles.filter((role: string) => role !== roleValue);
      this.userForm.patchValue({ roles: updatedRoles });
    } else {
      // Add role
      const updatedRoles = [...currentRoles, roleValue];
      this.userForm.patchValue({ roles: updatedRoles });
    }
  }

  removeRole1(roleValue: string, event: Event): void {
    event.stopPropagation();

    const currentRoles = this.userForm.get('roles')?.value || [];
    const updatedRoles = currentRoles.filter((role: string) => role !== roleValue);
    this.userForm.patchValue({ roles: updatedRoles });
  }

  isRoleSelected1(roleValue: string): boolean {
    const currentRoles = this.userForm.get('roles')?.value || [];
    return currentRoles.includes(roleValue);
  }

  getSelectedRoles1(): any[] {
    const currentRoles = this.userForm.get('roles')?.value || [];
    return this.availableRoles.filter(role => currentRoles.includes(role));
  }
}
